import * as d3 from 'd3'
// import {responsivefy} from "@/common/functions";
import axios from 'axios'

/**
 * Helper function to map ETL status to a color
 */
function getStatusColor(status) {
    switch (status) {
        case 'success':
            return '#28a745'; // Green
        case 'failure':
            return '#dc3545'; // Red
        case 'error':
            return '#dc3545'; // Red
        case 'started':
        case 'running':
            return '#007bff'; // Blue
        case 'not_started':
        case 'pending':
        default:
            return '#bfbfbf'; // Gray
    }
}
// At the top of your script (outside the export function)
let globalRefreshInterval = null;
let globalTextInterval = null;
export default function (chart, divId, vizName, resData,paramName, gridflag) {
    // --- 1. Define variables ---
    let svg, headerSvg,gLink, gNode;
    let idCounter = 0;
    const duration = 350;
    const nodeHeight = 20;
    const X_OFFSET = 20;
    const EXPANDED_NODES_KEY = 'etl_expanded_node_keys';

    // --- 2. Clear old SVG and Content ---
    const container = d3.select(divId);
    container.selectAll('*').remove(); // Clear everything, not just 'svg'

    // Ensure container is positioned for sticky context
    container
        .style('position', 'relative')
        .style('overflow', 'auto') // Ensure the container scrolls
        .style('height', '100%');  // Or specific height if needed


    // --- 3. Setup Structure for Sticky Headers ---

    // A. Create Sticky Header Container
    const headerDiv = container.append('div')
        .attr('class', 'sticky-header')
        .style('position', 'sticky')
        .style('top', '0')
        .style('z-index', '10')
        .style('background-color', '#fff') // Crucial: hide scrolling nodes behind header
        .style('border-bottom', '1px solid #eee')
        .style('margin-left', '20px') // Match transform translation
        .style('width', 'fit-content'); // Allow it to grow with SVG width

    // NEW: Add the timestamp display
    const statusRow = headerDiv.append('div')
        // .style('padding', '5px 20px')
        .style('font-size', '11px')
        .style('color', '#666')
        .style('display', 'flex')
        .style('align-items', 'center');

    const statusDot = statusRow.append('span')
        .style('height', '8px')
        .style('width', '8px')
        .style('background-color', '#28a745')
        .style('border-radius', '50%')
        .style('display', 'inline-block')
        .style('margin-right', '8px');

    const statusText = statusRow.append('span')
        .text('Initializing...');

    let lastFetchTime = new Date();

    function updateStatusText() {
        const now = new Date();
        const diffSeconds = Math.floor((now - lastFetchTime) / 1000);

        if (diffSeconds < 5) {
            statusText.text("Just updated");
        } else if (diffSeconds < 60) {
            statusText.text(`Updated ${diffSeconds} sec ago`);
        } else {
            const mins = Math.floor(diffSeconds / 60);
            statusText.text(`Updated ${mins} min ago`);
        }
    }

    // B. Create Header SVG
    headerSvg = headerDiv.append('svg')
        .attr('height', 20) // Fixed height for headers
        .attr('width', chart.width + 2 * chart.margin.left + chart.margin.right);

    // C. Create Body SVG (The Tree)
    svg = container
        .append('svg')
        .attr('width', chart.width + 2 * chart.margin.left + chart.margin.right)
        .attr('height', chart.height + 5 * chart.margin.top + chart.margin.bottom)
        .attr("transform", "translate(" + 20 + "," + 0 + ")"); // Removed top margin translation for svg, handled by header height

    gLink = svg.append('g').attr('fill', 'none').attr('stroke', '#999').attr('stroke-width', 1.5);
    gNode = svg.append('g');

    // --- 4. Add Column Headers (will be updated dynamically) ---
    const columns = chart.vizSpecific7.columns || [];
    const dimensionOrder = chart.vizSpecific7.dimensionOrder || [];
    const hyperlink = chart.vizSpecific7.hyperlink;
    const lastDimensionKey = dimensionOrder.length > 0 ? dimensionOrder[dimensionOrder.length - 1] : null;
    const MIN_COLUMN_SPACING = 110; // Minimum spacing between columns
    let selectedNodeId = null;

    console.log("columns",columns)
    console.log("Last dimension:", lastDimensionKey);

    // Store original columns for reference (will be updated in update function)
    let adjustedColumns = [...columns];

    console.log("res", chart, gridflag);

    function getExpandedNodesFromStorage() {
        try {
            return new Set(JSON.parse(localStorage.getItem(EXPANDED_NODES_KEY)) || []);
        } catch {
            return new Set();
        }
    }

    function saveExpandedNodesToStorage(set) {
        localStorage.setItem(EXPANDED_NODES_KEY, JSON.stringify([...set]));
    }

    function getNodeKey(node) {
        return node.ancestors()
            .reverse()
            .map(d => d.data?.name)
            .join('/');
    }



    // --- 5. Data Setup ---
    function buildHierarchy(data, chart) {
        if (!Array.isArray(data) || data.length === 0) {
            return { name: chart?.y1AxisDisplayName || "Root", children: [] };
        }

        // Helper to format seconds into HH:MM:SS
        function formatElapsed(seconds) {
            const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
            const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
            const s = Math.floor(seconds % 60).toString().padStart(2, '0');
            return `${h}:${m}:${s}`;
        }

        function groupBy(data, dimensionKeys, level = 0) {
            const key = dimensionKeys[level];
            const map = new Map();

            data.forEach(row => {
                const groupKey = row[key] ?? "Unknown";
                if (!map.has(groupKey)) map.set(groupKey, []);
                map.get(groupKey).push(row);
            });

            const result = [];

            map.forEach((rows, groupName) => {
                if (!rows || rows.length === 0) return;

                // --- TIME CALCULATIONS ---
                // 1. Get all valid start and end times for this node's rows
                const startTimes = rows.map(r => r.dim8_name).filter(t => t && t !== "-").map(t => new Date(t).getTime());
                const endTimes = rows.map(r => r.dim9_name).filter(t => t && t !== "-").map(t => new Date(t).getTime());

                let minStart = "-";
                let maxEnd = "-";
                let elapsedStr = "00:00:00";

                if (startTimes.length > 0 && endTimes.length > 0) {
                    const minStartTime = Math.min(...startTimes);
                    const maxEndTime = Math.max(...endTimes);

                    // Convert back to your string format (YYYY-MM-DD HH:MM:SS)
                    const formatDate = (ts) => {
                        const d = new Date(ts);
                        return d.getFullYear() + "-" +
                            (d.getMonth() + 1).toString().padStart(2, '0') + "-" +
                            d.getDate().toString().padStart(2, '0') + " " +
                            d.getHours().toString().padStart(2, '0') + ":" +
                            d.getMinutes().toString().padStart(2, '0') + ":" +
                            d.getSeconds().toString().padStart(2, '0');
                    };

                    minStart = formatDate(minStartTime);
                    maxEnd = formatDate(maxEndTime);

                    // Calculate duration in seconds
                    const diffSeconds = (maxEndTime - minStartTime) / 1000;
                    elapsedStr = formatElapsed(diffSeconds > 0 ? diffSeconds : 0);
                }

                // --- STATUS & METRICS ---
                const errorRow = rows.find(row => row?.dim4_name && row.dim4_name.toLowerCase().includes('error'));
                const baseRow = errorRow || rows[0];

                const nodeData = {
                    name: groupName || "Unknown",
                    metric1: rows.reduce((sum, r) => sum + (r.metric1 || 0), 0),
                    metric2: (baseRow?.metric2 ?? 0),
                    metric3:(baseRow?.metric3 ?? 0),
                    dim4_name: baseRow?.dim4_name ?? "",
                    dim5_name: baseRow?.dim5_name ?? "",
                    dim6_name: baseRow?.dim6_name ?? "",
                    dim7_name: baseRow?.dim7_name ?? "",
                    dim8_name: minStart,      // Computed Min Start Time
                    dim9_name: maxEnd,        // Computed Max End Time
                    dim10_name:baseRow?.dim10_name,
                    dim11_name: elapsedStr,   // Computed Elapsed Time
                    hyperlink: baseRow?.hyperlink ?? "",
                };

                if (level === dimensionKeys.length - 1) {
                    nodeData.children = [];
                } else {
                    nodeData.children = groupBy(rows, dimensionKeys, level + 1);
                }

                result.push(nodeData);
            });

            return result;
        }

        const dimensionOrder = chart.vizSpecific7.dimensionOrder || [];
        const children = groupBy(data, dimensionOrder);

        return {
            name: chart?.y1AxisDisplayName || "Root",
            children
        };
    }


    // Add this inside your export default function
    async function refreshData() {
        console.log("Fetching live ETL updates...");
        try {
            // 1. Fetch the latest data (Replace with your actual API endpoint)
            const response = await axios.post('/api/fetchDataWithParam/'+ chart.data.datasqlId + '/' + 'CommonVizClass',paramName);
            // const newData = await response.json();

            // 2. Update the timestamp
            lastFetchTime = new Date();
            // For demonstration, assuming resData is updated via your parent component/source
            // We rebuild the tree hierarchy with the new data
            let newTreeData = buildHierarchy(response.data, chart);

            // 2. Map the new data to the existing root structure
            // D3 hierarchy maintains identity if the data structure is stable
            const newRoot = d3.hierarchy(newTreeData, d => d.children);

            // 3. Update the data bound to 'root' without losing state
            // We transfer the status of children/collapsed nodes to the new root
            syncNodeState(root, newRoot);

            // 4. Trigger update to animate changes
            update(root);

        } catch (error) {
            console.error("Live update failed:", error);
        }
    }

    /**
     * Helper to sync expansion state between old and new data trees
     */
    function syncNodeState(oldNode, newNode) {
        // Transfer the internal expansion state
        if (oldNode.children) {
            newNode.children = newNode.children || []; // Ensure children exist
        } else if (oldNode._children) {
            newNode._children = newNode.children;
            newNode.children = null;
        }

        // Recursively sync children based on a unique identifier (like name)
        if (newNode.children || newNode._children) {
            const nextChildren = newNode.children || newNode._children;
            const prevChildren = oldNode.children || oldNode._children;

            if (prevChildren) {
                nextChildren.forEach(child => {
                    const match = prevChildren.find(p => p.data.name === child.data.name);
                    if (match) syncNodeState(match, child);
                });
            }
        }

        // Update the reference
        Object.assign(oldNode.data, newNode.data);
    }

    console.log("resData", resData);



    let treeData = buildHierarchy(resData, chart);
    console.log("Tree Data:", treeData);
    let expandedNodeKeys = getExpandedNodesFromStorage();



    var root = d3.hierarchy(treeData, function (d) {
        return d.children;
    })

    // Initialize positions
    root.x0 = 0;
    root.y0 = chart.margin.top;

    // 1️⃣ Collapse everything first
    root.descendants().forEach((d) => {
        d._children = d.children;
        if (d.depth > 0) {
            d.children = null;
        }
    });

/// 2️⃣ Recursive Restoration (The Fix)
    function restoreExpansion(node) {
        const key = getNodeKey(node);

        // If this node's key is in our storage, expand it
        if (expandedNodeKeys.has(key)) {
            if (node._children) {
                node.children = node._children;
                node._children = null;
            }

            // If it has children (now expanded), check them recursively
            if (node.children) {
                node.children.forEach(restoreExpansion);
            }
        } else if (node._children) {
            // Even if not expanded, we might have nested expanded nodes
            // that were saved. Check _children to be safe.
            node._children.forEach(restoreExpansion);
        }
    }

// Start restoration from root
    restoreExpansion(root);


    // --- 6. Define Core Update/Click Functions ---

    /**
     * Calculate tree layout - simple sequential positioning
     */
    function calculateLayout(source) {
        console.log(source)
        let yPosition = chart.margin.top;

        function traverse(node, depth) {
            // Set position
            node.x = depth * 30 + X_OFFSET; // Horizontal indentation
            node.y = yPosition + nodeHeight / 2; // Center the node

            // Store absolute Y position for column alignment
            node.absoluteY = yPosition + nodeHeight / 2;

            yPosition += nodeHeight; // Move down for next node

            // Process children if they are visible
            if (node.children) {
                node.children.forEach(child => traverse(child, depth + 1));
            }
        }

        yPosition = chart.margin.top;
        traverse(root, 0);

        return yPosition; // Return total height used
    }

    /**
     * Proper hierarchical link path
     */
    function getLinkPath(d) {
        const sourceX = d.source.x;
        const sourceY = d.source.y;
        const targetX = d.target.x;
        const targetY = d.target.y;

        // Create elbow connector: vertical then horizontal
        return `
      M${sourceX},${sourceY}
      V${targetY}
      H${targetX}
    `;
    }

    /**
     * Check if any descendant has dim4_name !== 'success' (including undefined/null/empty)
     */
    function hasNonSuccessDescendant(n) {
        // Check current node - anything that's not explicitly 'success' is non-success
        const status = n.data?.dim4_name;
        if (status !== 'success') {
            // If it's a leaf node (no children), undefined/null/empty counts as non-success
            if (!n.children && !n._children) {
                return true;
            }
            // If it's a parent node with a non-success status, return true
            if (status !== undefined && status !== null && status !== '') {
                return true;
            }
        }

        // Check children (visible)
        if (n.children) {
            for (const child of n.children) {
                if (hasNonSuccessDescendant(child)) {
                    return true;
                }
            }
        }

        // Check _children (collapsed/hidden)
        if (n._children) {
            for (const child of n._children) {
                if (hasNonSuccessDescendant(child)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Find the first non-success descendant status (including undefined/null/empty)
     */
    function findFirstNonSuccessStatus(n) {
        const status = n.data?.dim4_name;

        // If status exists and is not 'success', return it
        if (status && status !== 'success') {
            return status;
        }

        // If status is undefined/null/empty and this is a leaf node, return empty string
        // (which will be treated as non-success and show gray color)
        if ((status === undefined || status === null || status === '') && !n.children && !n._children) {
            return ''; // Return empty string to indicate non-success
        }

        // Check children (visible)
        if (n.children) {
            for (const child of n.children) {
                const childStatus = findFirstNonSuccessStatus(child);
                // Return if we found a non-success status (including empty string)
                if (childStatus !== null && childStatus !== 'success') {
                    return childStatus;
                }
            }
        }

        // Check _children (collapsed/hidden)
        if (n._children) {
            for (const child of n._children) {
                const childStatus = findFirstNonSuccessStatus(child);
                // Return if we found a non-success status (including empty string)
                if (childStatus !== null && childStatus !== 'success') {
                    return childStatus;
                }
            }
        }

        return null;
    }

    /**
     * Check if any descendant has dim4_name !== 'success'
     * Returns the effective status considering descendants
     */
    function getEffectiveStatus(node) {
        const ownStatus = node.data?.dim4_name;
        const ownBeginStatus = node.data?.dim5_name;

        // --- NEW RULES (highest priority) ---

        // Case 1: both are null / undefined
        if (
            (ownBeginStatus === undefined || ownBeginStatus === null) &&
            (ownStatus === undefined || ownStatus === null)
        ) {
            return null;
        }

        // Case 2: started but no final status yet
        if (
            ownBeginStatus === 'started' &&
            (ownStatus === undefined || ownStatus === null)
        ) {
            return 'started';
        }

        // ✅ NEW: if ownStatus contains 'error', return error immediately
        if (
            typeof ownStatus === 'string' &&
            ownStatus.toLowerCase().includes('error')
        ) {
            return 'error';
        }

        // --- EXISTING LOGIC (unchanged) ---

        // If own status is undefined/null/empty, check descendants
        if (ownStatus === undefined || ownStatus === null || ownStatus === '') {
            if (hasNonSuccessDescendant(node)) {
                const descendantStatus = findFirstNonSuccessStatus(node);
                if (descendantStatus !== null) {
                    if (
                        typeof descendantStatus === 'string' &&
                        descendantStatus.toLowerCase().includes('error')
                    ) {
                        return 'error';
                    }else{
                        return descendantStatus;
                    }
                }
            }
            return ''; // gray
        }

        // If own status is 'success', check descendants
        if (ownStatus === 'success' && hasNonSuccessDescendant(node)) {
            const descendantStatus = findFirstNonSuccessStatus(node);
            if (descendantStatus !== null) {
                if (
                    typeof descendantStatus === 'string' &&
                    descendantStatus.toLowerCase().includes('error')
                ) {
                    return 'error';
                }else{
                   return descendantStatus;
                }
            }
        }

        // Return own status
        return ownStatus;
    }


    /**
     * Recursively expand or collapse all descendants of a node
     */
    function toggleAllDescendants(node, shouldExpand) {
        if (!node) return;

        // Toggle current node's children
        if (shouldExpand) {
            // Expand: move _children to children
            if (node._children) {
                node.children = node._children;
                node._children = null;
            }
        } else {
            // Collapse: move children to _children
            if (node.children) {
                node._children = node.children;
                node.children = null;
            }
        }

        // Recursively process all descendants
        // After toggling, use children if expanding, _children if collapsing
        const childrenToProcess = shouldExpand ? node.children : node._children;
        if (childrenToProcess && childrenToProcess.length > 0) {
            childrenToProcess.forEach(child => {
                toggleAllDescendants(child, shouldExpand);
            });
        }
    }

    /**
     * Toggles children on click
     */
    function click(event, datum) {
        const node = (datum && datum.data)
            ? datum
            : d3.select(this).datum();

        if (!node || !node.data) return;

        selectedNodeId = node.id;
        const key = getNodeKey(node);

        let changed = false;

        const isParentNode = node.depth === 1;

        if (isParentNode) {
            const shouldExpand = !node.children;
            console.log("should expand",shouldExpand);
            toggleAllDescendants(node, shouldExpand);

            // collect descendants ONLY ONCE
            const descendants = [];
            (function collect(n) {
                descendants.push(n);
                (n.children || n._children || []).forEach(collect);
            })(node);

            console.log("descents",descendants)

            descendants.forEach( (d)=> {
                console.log("d",d)
                if (shouldExpand) {
                    expandedNodeKeys.add(getNodeKey(d));
                } else {
                    expandedNodeKeys.delete(getNodeKey(d));
                }
            });

            changed = true;
        } else {
            if (node.children) {
                node._children = node.children;
                node.children = null;
                expandedNodeKeys.delete(key);
                changed = true;
            } else if (node._children) {
                node.children = node._children;
                node._children = null;
                expandedNodeKeys.add(key);
                changed = true;
            }
        }

        // 🔥 WRITE ONLY IF CHANGED
        if (changed) {
            saveExpandedNodesToStorage(expandedNodeKeys);
        }

        update(node);
    }




    /**
     * Calculate column positions based on visible nodes
     */
    function calculateColumnPositions(visibleNodes) {
        // Create a temporary text element to measure text width
        const tempText = svg.append('text')
            .style('visibility', 'hidden')
            .style('font-size', '12px')
            .attr('font-family', 'Arial, sans-serif');

        // Calculate maximum width for the last dimension column FIRST
        // This will be used as dynamic padding between node labels and first data column
        let maxLastDimensionWidth = MIN_COLUMN_SPACING;
        if (lastDimensionKey && visibleNodes && visibleNodes.length > 0) {
            // Get all visible values for the last dimension
            const lastDimensionValues = visibleNodes
                .map(node => {
                    const v = node.data[lastDimensionKey];
                    return v != null ? String(v) : '--';
                });

            // Calculate maximum width
            lastDimensionValues.forEach(val => {
                tempText.text(val);
                const bbox = tempText.node().getBBox();
                if (bbox.width > maxLastDimensionWidth) {
                    maxLastDimensionWidth = bbox.width;
                }
            });

            // Add padding to max width for column spacing
            maxLastDimensionWidth = Math.max(maxLastDimensionWidth + 20, MIN_COLUMN_SPACING);
        }

        // Calculate maximum width for node labels (name column)
        let maxNodeLabelWidth = 0;
        if (visibleNodes && visibleNodes.length > 0) {
            visibleNodes.forEach(node => {
                const nodeName = node.data?.name || '';
                if (nodeName) {
                    tempText.text(nodeName);
                    const bbox = tempText.node().getBBox();
                    // Calculate total width: node.x (position) + 12 (offset from circle) + text width
                    const totalWidth = node.x + 12 + bbox.width;
                    if (totalWidth > maxNodeLabelWidth) {
                        maxNodeLabelWidth = totalWidth;
                    }
                }
            });
        }

        // Use the last dimension's max width as dynamic padding after node labels
        // This ensures the first column starts far enough to accommodate the longest last dimension value
        const nodeLabelEndX = maxNodeLabelWidth + maxLastDimensionWidth;

        // Remove temporary text element
        tempText.remove();

        // Adjust column x positions
        const newAdjustedColumns = [];
        columns.forEach((col, index) => {
            let newX = col.x;

            if (index === 0) {
                // First data column should start after node labels
                newX = Math.max(col.x, nodeLabelEndX);
            } else if (index > 0) {
                const prevCol = newAdjustedColumns[index - 1];
                // If this is the column for the last dimension, use calculated max width
                if (col.key === lastDimensionKey) {
                    newX = prevCol.x + maxLastDimensionWidth;
                } else {
                    // For other columns, maintain relative spacing but ensure minimum
                    const currentSpacing = col.x - columns[index - 1].x;
                    const minSpacing = Math.max(currentSpacing, MIN_COLUMN_SPACING);
                    newX = prevCol.x + minSpacing;
                }
            }

            newAdjustedColumns.push({ ...col, x: newX });
        });

        return { adjustedColumns: newAdjustedColumns, lastColumnWidth: maxLastDimensionWidth };
    }

    /**
     * The main D3 update function
     */
    function update(source) {
        const totalHeight = calculateLayout(source);

        // Update SVG height
        svg
            .attr('height', Math.max(600, totalHeight + chart.margin.bottom));

        const nodes = root.descendants();
        const links = root.links();

        // Calculate column positions based on currently visible nodes
        const columnData = calculateColumnPositions(nodes);
        adjustedColumns = columnData.adjustedColumns;

        // Update SVG width if needed
        const maxColumnX = Math.max(...adjustedColumns.map(col => col.x));
        const requiredWidth = maxColumnX + columnData.lastColumnWidth + chart.margin.right;
        const currentWidth = chart.width + 2 * chart.margin.left + chart.margin.right;

        if (requiredWidth > currentWidth) {
            svg.attr('width', requiredWidth);
        }

        // Update column headers
        headerSvg.selectAll('.column-header').remove();
        adjustedColumns.forEach((col) => {
            headerSvg
                .append('text')
                .attr('class', 'column-header')
                .attr('dy', '0.32em')
                .attr('y', chart.margin.top - 20)
                .attr('x', col.x)
                .attr('text-anchor', 'start')
                .attr('font-weight', 'bold')
                .text(col.label);
        });

        // --- Row Highlight Layer ---
        const rowHeight = nodeHeight; // you already use this
        // const highlightPadding = 6;

// Bind highlight rects
        const rowHighlight = gNode
            .selectAll('rect.row-highlight')
            .data(nodes, d => d.id);

// ENTER
        rowHighlight.enter()
            .insert('rect', ':first-child') // behind node
            .attr('class', 'row-highlight')
            .attr('x', 0)
            .attr('y', d => d.absoluteY - rowHeight / 2)
            .attr('height', rowHeight)
            .attr('rx', 4)
            .attr('ry', 4)
            .attr('width', svg.attr('width'))
            .attr('fill', d => d.id === selectedNodeId ? '#eef4ff' : 'transparent');

// UPDATE
        rowHighlight
            .transition()
            .duration(duration)
            .attr('y', d => d.absoluteY - rowHeight / 2)
            .attr('width', svg.attr('width'))
            .attr('fill', d => d.id === selectedNodeId ? '#eef4ff' : 'transparent');

// EXIT
        rowHighlight.exit().remove();


        // --- 1. Update Nodes ---
        const node = gNode
            .selectAll('g.node')
            .data(nodes, (d) => d.id || (d.id = ++idCounter));

        // **ENTER (Create new nodes)**
        const nodeEnter = node
            .enter()
            .append('g')
            .classed('node', true)
            .attr('transform', `translate(${source.x},${source.y})`)
            .style('opacity', 0)
            .style('cursor', 'pointer')
            .on('click', click);

        // Add Circle
        nodeEnter
            .append('circle')
            .attr('r', 6)
            .attr('fill', (d) => getStatusColor(getEffectiveStatus(d)));

        // Add Text Label
        nodeEnter
            .append('text')
            .classed('label', true)
            .attr('dy', '0.32em')
            .attr('x', 12)
            .attr('fill', (d) => getStatusColor(getEffectiveStatus(d)))
            .attr('font-weight', (d) => (d._children || d.children) ? 'bold' : 'normal')
            .text((d) => d.data.name);


        nodeEnter
            .filter(d =>
                hyperlink === true &&
                d.depth === 1 &&
                (d.children || d._children) &&
                d.data?.hyperlink
            )
            .append('text')
            .attr('class', 'parent-icon')
            .attr('x', 32)
            .attr('dy', '0.32em')
            .attr('dx', d => (d.data?.name?.length || 0) * 7 + 6)
            .style('font-family', '"Font Awesome 5 Free"')
            .style('font-weight', '900')
            .style('font-size', '11px')
            .style('fill', '#4785ee')
            .style('cursor', 'pointer')
            .text('\uf35d') // 👁 view icon
            .on('click', function (event) {
                if (event && event.stopPropagation) {
                    event.stopPropagation();
                } else if (d3.event && d3.event.stopPropagation) {
                    d3.event.stopPropagation();
                }

                const node = d3.select(this).datum();
                if (!node || !node.data || !node.data.hyperlink) return;

                window.open(node.data.hyperlink, '_blank');
            });







        // **UPDATE (Transition existing nodes)**
        const nodeUpdate = node
            .merge(nodeEnter)
            .transition()
            .duration(duration)
            .attr('transform', (d) => `translate(${d.x},${d.y})`)
            .style('opacity', 1);

        // Update circle
        nodeUpdate
            .select('circle')
            .attr('fill', (d) => getStatusColor(getEffectiveStatus(d)))
            .attr('r', (d) => (d._children || d.children) ? 6 : 4);

        // Update main label
        nodeUpdate
            .select('.label')
            .attr('fill', (d) => getStatusColor(getEffectiveStatus(d)))
            .attr('font-weight', (d) => (d._children || d.children) ? 'bold' : 'normal')
            .text((d) => d.data.name);

        // **EXIT (Remove old nodes)**
        node
            .exit()
            .transition()
            .duration(duration)
            .attr('transform', `translate(${source.x},${source.y})`)
            .style('opacity', 0)
            .remove();

        // --- 2. Update Column Texts (Separate from nodes for proper alignment) ---

        // --- 2. Update Column Texts (Dynamic based on columns array) ---

// Remove previous column text elements
        svg.selectAll('.column-text').remove();

// Loop through each column definition
        adjustedColumns.forEach((col) => {
            // Check if this column has boldAndNotRepeat property
            const shouldBoldAndNotRepeat = col.boldAndNotRepeat === true;

            // Pre-process nodes to determine which should show values and which should be bold
            if (shouldBoldAndNotRepeat) {
                nodes.forEach((node) => {
                    const value = node.data[col.key];
                    const parentValue = node.parent?.data?.[col.key];

                    node._showColumnValue = false;
                    node._makeColumnBold = false;

                    if (value != null && value !== '') {
                        if (value !== parentValue) {
                            node._showColumnValue = true;
                            node._makeColumnBold = true;
                        }
                    }
                });
            }



            svg.selectAll(`.column-text-${col.key}`)
                .data(nodes)
                .enter()
                .append('text')
                .attr('class', `column-text column-text-${col.key}`)
                .attr('dy', '0.32em')
                .attr('x', col.x)
                .attr('y', (d) => d.absoluteY)
                .attr('text-anchor', 'start')
                .attr('fill', (d) =>
                    col.key === "dim4_name" ? getStatusColor(getEffectiveStatus(d)) : "#555"
                )
                .attr('font-weight', () => {
                    if (shouldBoldAndNotRepeat) {
                        return 'bold';
                    }
                    return 'normal';
                })
                .text((d) => {
                    const v = d.data[col.key];

                    // 🔒 HARD RULE: Job Name only appears at depth 2
                    if (col.key === 'dim6_name' && d.depth===2) {
                        return d.depth === 2 && v != null ? v : '';
                    }

                    // Existing behavior for other columns
                    if (shouldBoldAndNotRepeat) {
                        return d._showColumnValue && v != null ? v : '';
                    }

                    return v != null ? v : '--';
                });

        });


        // --- 3. Update Links ---
        const link = gLink
            .selectAll('path')
            .data(links, (d) => d.target.id);

        // **ENTER (Create new links)**
        const linkEnter = link
            .enter()
            .insert('path', 'g')
            .attr('d', () => {
                const o = {
                    source: { x: source.x, y: source.y },
                    target: { x: source.x, y: source.y },
                };
                return getLinkPath(o);
            })
            .attr('stroke-opacity', 0);

        // **UPDATE (Transition existing links)**
        link
            .merge(linkEnter)
            .transition()
            .duration(duration)
            .attr('d', getLinkPath)
            .attr('stroke-opacity', 1);

        // **EXIT (Remove old links)**
        link
            .exit()
            .transition()
            .duration(duration)
            .attr('d', () => {
                const o = {
                    source: { x: source.x, y: source.y },
                    target: { x: source.x, y: source.y },
                };
                return getLinkPath(o);
            })
            .attr('stroke-opacity', 0)
            .remove();
    }

    // --- 7. Initial Draw ---
    update(root);
    // --- 8. Prevent Duplicate Timers ---
    if (globalRefreshInterval) clearInterval(globalRefreshInterval);
    if (globalTextInterval) clearInterval(globalTextInterval);

    // Start only ONE instance
    globalRefreshInterval = setInterval(async () => {
        refreshData();              // Your D3 update logic
        // Visual "pulse" to show a refresh happened
        statusDot.transition().duration(200).style('opacity', 0).transition().duration(200).style('opacity', 1);
    }, 60000);

    globalTextInterval = setInterval(() => {
        updateStatusText();
    }, 1000);


    return () => {
        console.log("Intervals cleared by cleanup");
        clearInterval(globalRefreshInterval);
        clearInterval(globalTextInterval);
        globalRefreshInterval = null;
        globalTextInterval = null;
    };
}